import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { SearchableSelect, type SearchableSelectOption } from "../../components/ui/searchable-select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { cn } from "../../components/lib/utils";
import { motion, useAnimation } from "framer-motion";
import {
  Search,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { datePickerStyles } from "../../components/constants/styles";
import "react-datepicker/dist/react-datepicker.css";
import { getPublicJobAppliedList, getSubJobAppliedList, updatePublicJobApplication, updateSubJobApplication } from "../../services/jobsapi";
import { useRbac } from "../../context";

interface Application {
  _id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  jobTitle: string;
  appliedAt: string;
  status: string;
}

interface PaginationState {
  page: number;
  limit: number;
  totalPages: number;
  totalResults: number;
}

export function ExternalJobApplications() {
const navigate = useNavigate();
  const controls = useAnimation();
  // Inject custom styles for DatePicker
  useEffect(() => {
    
    if (typeof document !== "undefined" && datePickerStyles) {
      const styleSheet = document.createElement("style");
      styleSheet.innerText = datePickerStyles;
      document.head.appendChild(styleSheet);
      return () => {
        if (document.head.contains(styleSheet)) {
          document.head.removeChild(styleSheet);
        }
      };
    }
  }, []);

  // State management
  const [applications, setApplications] = useState<Application[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalResults: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedJob, setSelectedJob] = useState("all");
  const [selectedDateFrom, setSelectedDateFrom] = useState("");
  const [selectedDateTo, setSelectedDateTo] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [jobs, setJobs] = useState<any[]>([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [loadingId, setLoadingId] = useState("");

  // Mock data for jobs and applications
  const jobOptions: SearchableSelectOption[] = useMemo(() => {
    const options: SearchableSelectOption[] = [{ value: "all", label: "Status" },{ value: "applied", label: "Applied" },{ value: "shortlisted", label: "Shortlisted" },{ value: "rejected", label: "Rejected" }];

    return options;
  }, [ isLoadingJobs]);

  // Mock fetch jobs
  const fetchJobs = useCallback(async () => {
    setIsLoadingJobs(true);
    try {
      // Mock job data
      const mockJobs = [
        { id: "1", title: "Software Engineer" },
        { id: "2", title: "Product Manager" },
        { id: "3", title: "Data Analyst" },
      ];
      setJobs(mockJobs);
    } catch (err: any) {
      console.error("Failed to fetch jobs:", err);
      setJobs([]);
      toast.error("Failed to load jobs for filtering");
    } finally {
      setIsLoadingJobs(false);
    }
  }, []);

  // Mock fetch applications
  const fetchApplications = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const query = {
        search:searchTerm,
        selectedDateFrom,
        selectedDateTo,
        skip:(pagination.page -1)*10,
        status:selectedJob,
        isExternalJob: true
      }
      const jobs = await getPublicJobAppliedList(query);
      setApplications(jobs[0].data);
      setPagination((prev) => ({
        ...prev,
        totalPages: Math.ceil(jobs[0]?.metadata[0]?.totalResults / 10),
        totalResults: jobs[0]?.metadata[0]?.totalResults || 0,
      }));
    } catch (err: any) {
      setError(err.message || "Failed to fetch applications");
      toast.error(err.message || "Failed to fetch applications", { duration: 4000 });
    } finally {
      setIsLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm, selectedJob, selectedDateFrom, selectedDateTo, sortBy, sortOrder]);

  useEffect(() => {
    fetchApplications();
    fetchJobs();
  }, [fetchApplications, fetchJobs]);

  // Debounced search
  const debounce = useCallback((fn: (...args: any[]) => void, delay: number) => {
    let timer: NodeJS.Timeout | null = null;
    return (...args: any[]) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        fn(...args);
        timer = null;
      }, delay);
    };
  }, []);

  const debouncedSearch = useMemo(
    () => debounce((value: string) => setSearchTerm(value), 500),
    [debounce]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchTerm(value);
      debouncedSearch(value);
      fetchApplications()
    },
    [debouncedSearch]
  );

  // Date range validation
  const isValidDateRange = useCallback(() => {
    if (!selectedDateFrom || !selectedDateTo) return true;
    return new Date(selectedDateFrom) <= new Date(selectedDateTo);
  }, [selectedDateFrom, selectedDateTo]);

  const [dateError, setDateError] = useState<string | null>(null);

  useEffect(() => {
    if (!isValidDateRange()) {
      setDateError("End date cannot be before start date.");
    } else {
      setDateError(null);
    }
  }, [selectedDateFrom, selectedDateTo, isValidDateRange]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page }));
      fetchApplications()
    }
  };

  // Toggle logic for Accept/Reject
const handleAccept = async (_id: string) => {
  try {
    setLoadingId(_id)
    await updatePublicJobApplication(_id, "shortlisted");
    

    setApplications((prev) =>
      prev.map((app) =>
        app._id === _id
          ? { ...app, status: "shortlisted" }
          : app
      )
    );
    setLoadingId("")
    toast.success(`Application ${_id} accepted!`);
  } catch (error: any) {
    // Check if it's an Axios error with a status code
    setLoadingId("")
    if (error.response?.status === 409) {
      toast.error("This application already exists or is duplicated.");
    } else {
      toast.error(error.response?.data?.message || "Something went wrong");
    }
  }
};


  const handleReject = async(_id: string) => {
   
    await updatePublicJobApplication(_id,'rejected')
    setApplications((prev) =>
      prev.map((app) =>
        app._id === _id
          ? {
            ...app,
            status: "rejected"
          }
          : app
      )
    );
    toast.error(
      `Application ${_id} ${applications.find((a) => a._id === _id)?.status === "Rejected"
        ? "set to Applied"
        : "rejected!"
      }`
    );
  };

  const SortableHeader: React.FC<{ column: string; children: React.ReactNode; className?: string }> = ({
    column,
    children,
    className,
  }) => (
    <th
      className={cn(
        "px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors sticky top-0 bg-[#FFF5F2] z-10",
        className
      )}
      onClick={() => {
        if (sortBy === column) {
          setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
          setSortBy(column);
          setSortOrder("asc");
        }
      }}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortBy === column && (
          sortOrder === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        )}
      </div>
    </th>
  );
  const user = useRbac()
  

  return (
    <div>
      <div className="bg-white border-b border-gray-200 px-3 sm:px-6 py-4 flex">
        <div>
          <Button variant="ghost" size="sm" className="p-1 sm:p-2" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </div>
        <div className="w-full">
          <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">Job Applications</h1>
                <p className="text-xs sm:text-sm text-gray-500">Manage and track public job applications efficiently</p>
              </div>
            </div>
            <div className="relative flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search Applications..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="pl-10 w-full sm:w-64 border-gray-300"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <SearchableSelect
              options={jobOptions}
              value={selectedJob}
              onValueChange={(v) => {
                setSelectedJob(v);
                fetchApplications()
                setPagination((p) => ({ ...p, page: 1 }));
              }}
              placeholder={isLoadingJobs ? "Loading jobs..." : "All Jobs"}
              emptyText="No jobs found"
              searchPlaceholder="Search jobs..."
              className="w-37 h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
              searchable
            />
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={selectedDateFrom}
                onChange={(e) => {

                  setSelectedDateFrom(e.target.value);
                  fetchApplications()
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
                max={selectedDateTo || undefined}
                className="w-full sm:w-40 h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
                title="From Date"
              />
              <Input
                type="date"
                value={selectedDateTo}
                onChange={(e) => {
                  setSelectedDateTo(e.target.value);
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
                min={selectedDateFrom || undefined}
                className="w-full sm:w-40 h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
                title="To Date"
              />
            </div>
          </div>
          {dateError && <p className="text-red-500 text-sm mt-2">{dateError}</p>}
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-background relative">
        <div className="h-[calc(100vh-275px)] overflow-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead style={{ backgroundColor: "#FFF5F2" }}>
              <tr>
                <th className="px-4 py-3 w-12 sticky top-0 bg-[#FFF5F2] z-10"></th>
                <SortableHeader column="candidate.name" className="min-w-48 sticky left-0 bg-[#FFF5F2] z-20 border-r border-gray-200">
                  Name
                </SortableHeader>
                <SortableHeader column="companyName" className="bg-[#FFF5F2] min-w-48">Company Name</SortableHeader>
                <SortableHeader column="jobTitle" className="bg-[#FFF5F2] min-w-48">Job Title</SortableHeader>
                <SortableHeader column="candidate.phone" className="bg-[#FFF5F2] min-w-32">Phone</SortableHeader>
                <SortableHeader column="candidate.email" className="bg-[#FFF5F2] min-w-64">Email</SortableHeader>
                <SortableHeader column="appliedAt" className="bg-[#FFF5F2] min-w-40">Applied At</SortableHeader>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-32 sticky top-0 bg-[#FFF5F2] z-10">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <div className="flex items-center justify-center gap-2 text-gray-600">
                      <svg
                        className="animate-spin h-5 w-5 text-orange-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        ></path>
                      </svg>
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <p className="text-gray-500">{error}</p>
                  </td>
                </tr>
              ) : applications.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <p className="text-gray-500">No applications found matching your criteria.</p>
                  </td>
                </tr>
              ) : (
                applications.map((app, index) => (
                  <tr
                    key={app._id}
                    className={cn("hover:bg-gray-50 transition-colors cursor-pointer")}
                  >
                    <td className="px-4 py-4"></td>
                    <td className="px-4 py-4 sticky left-0 bg-white z-10 border-r border-gray-200">
                      <span className="font-medium text-gray-900">{app.name}</span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">{app.company}</td>
                    <td className="px-4 py-4 text-sm text-gray-900">{app.jobTitle}</td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      <span
                        className="cursor-pointer hover:text-orange-600"
                        title="Copy phone number"
                        onClick={(e) => {
                           if(! (user.role === "COMPANY" && index > 4) ){
                          e.stopPropagation();
                          navigator.clipboard.writeText(app.phone);
                          toast.success("Phone number copied!");
                        }}
                      }
                      >
                        {user.role === "COMPANY" && index > 4 
                          ? `${app.phone.slice(0, 2)}xxxxxx${app.phone.slice(-2)}` 
                          : app.phone}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      <span
                        className="cursor-pointer hover:text-orange-600"
                        title="Copy email"
                        onClick={(e) => {
                          if(! (user.role === "COMPANY" && index > 4) ){
                            e.stopPropagation();
                            navigator.clipboard.writeText(app.email);
                            toast.success("Email copied!");
                          }
                        }}
                      >
                        {user.role === "COMPANY" && index > 4 
                        ? `${app.email.slice(0, 4)}xxxxxx${app.email.slice(-4)}` 
                        : app.email}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {new Date(app.appliedAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex gap-2">
                        {app.status === "applied" ? user.role ==="ADMIN" ? <span color="orange">applied</span>  : <><Button
                        variant="outline"
                        size="sm"
                        className={cn(
                          "rounded-md border border-orange-300 px-3 py-1 text-sm font-medium text-orange-500 bg-white hover:bg-orange-50"
                        )}
                        disabled={loadingId == app._id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAccept(app._id);
                        }}
                      >
                         Shortlist
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className={cn(
                          "rounded-md border border-orange-300 px-3 py-1 text-sm font-medium text-orange-500 bg-white hover:bg-orange-50"
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReject(app._id);
                        }}
                      >
                        Reject
                      </Button></> : <>{app.status === "shortlisted" ? <span style={{color:"green"}}>Shortlisted</span> : <span style={{color:"red"}}>Rejected</span>}</>}
                     
                    </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between gap-2 justify-end p-4 pl-8">
          <div className="text-sm text-gray-500">
            Showing {applications.length} of {pagination.totalResults} applications
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1 || isLoading}
              className="rounded-lg border-gray-200 hover:bg-orange-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
              .filter(
                (pageNum) =>
                  pageNum === 1 ||
                  pageNum === pagination.totalPages ||
                  (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2)
              )
              .map((pageNum) => (
                <Button
                  key={pageNum}
                  variant={pagination.page === pageNum ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePageChange(pageNum)}
                  disabled={isLoading || pagination.page == pageNum}
                  className={
                    pagination.page === pageNum
                      ? "bg-orange-600 text-white hover:bg-orange-700"
                      : "border-gray-200 hover:bg-orange-50"
                  }
                >
                  {pageNum}
                </Button>
              ))}
            {pagination.totalPages > 5 && pagination.page < pagination.totalPages - 2 && (
              <span className="text-sm text-gray-500">...</span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages || isLoading}
              className="rounded-lg border-gray-200 hover:bg-orange-50"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div> 
    )
}

export default function ViewApplications({ onAddCandidate }: { onAddCandidate?: () => void }) {
  return <ExternalJobApplications />;
}